import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { Star, Trash2, Sparkles, MessageSquare, Loader2, Search, CheckCircle2, Download, RefreshCw, AlertTriangle, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { PendingImports } from '../components/PendingImports';

export const Reviews = () => {
  const { selectedClient, API } = useClient();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pasteText, setPasteText] = useState('');
  const [pasting, setPasting] = useState(false);
  const [pasteResult, setPasteResult] = useState(null);

  // Google import state
  const [googleStatus, setGoogleStatus] = useState({ linked: false, place_id: null });
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [placesError, setPlacesError] = useState(null);
  const searchTimer = useRef(null);

  const load = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/reviews/${selectedClient.id}`),
        axios.get(`${API}/reviews/${selectedClient.id}/google-status`),
      ]);
      setReviews(r1.data.reviews);
      setGoogleStatus(r2.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); setSearchQ(''); setSearchResults([]); setImportResult(null); }, [selectedClient]);

  // Debounced Places typeahead — state setters close over latest setX refs via useCallback
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true); setPlacesError(null);
    try {
      const res = await axios.get(`${API}/benchmarks/places/search`, { params: { q } });
      if (res.data.success) {
        setSearchResults(res.data.results || []);
      } else {
        setSearchResults([]);
        setPlacesError(res.data.error_detail || res.data.message);
      }
    } catch (e) {
      setSearchResults([]);
      setPlacesError(e?.response?.data?.detail || e.message);
    } finally { setSearching(false); }
  }, [API]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(searchQ), 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchQ, runSearch]);

  const importFromPlace = async (placeId) => {
    setImporting(true); setImportResult(null);
    try {
      const res = await axios.post(`${API}/reviews/${selectedClient.id}/import-google`, { place_id: placeId, save_place_id: true });
      setImportResult(res.data);
      setSearchResults([]);
      setSearchQ('');
      await load();
    } catch (e) {
      setImportResult({ error: e?.response?.data?.detail || e.message });
    } finally { setImporting(false); }
  };

  const refreshLinked = async () => {
    if (!googleStatus.place_id) return;
    await importFromPlace(googleStatus.place_id);
  };

  const unlinkGoogle = async () => {
    const wipe = window.confirm(
      'Unlink this client from Google?\n\n' +
      'Click OK to also DELETE the reviews already imported from Google (start fresh).\n' +
      'Click Cancel to just unlink and KEEP the imported reviews.'
    );
    // wipe===true means user clicked OK (delete), false means cancel (keep)
    try {
      await axios.delete(`${API}/reviews/${selectedClient.id}/google-link`, { params: { also_delete_imported: wipe } });
      setGoogleStatus({ linked: false, place_id: null });
      setImportResult(null);
      setSearchQ('');
      setSearchResults([]);
      await load();
    } catch (e) {
      alert('Unlink failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const submitPaste = async () => {
    if (!pasteText.trim()) return;
    setPasting(true); setPasteResult(null);
    try {
      const res = await axios.post(`${API}/reviews/${selectedClient.id}/paste`, { text: pasteText });
      setPasteResult(res.data);
      setPasteText('');
      load();
    } catch (e) {
      setPasteResult({ error: e?.response?.data?.detail || e.message });
    } finally { setPasting(false); }
  };

  const removeRow = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    await axios.delete(`${API}/reviews/${id}`);
    load();
  };

  if (!selectedClient) return <div className="p-8 text-text-muted">Select a client to view reviews.</div>;

  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.rating / (r.rating_max || 5) * 5), 0) / reviews.length).toFixed(2) : 0;
  const positive = reviews.filter(r => r.sentiment === 'positive').length;
  const negative = reviews.filter(r => r.sentiment === 'negative').length;

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">Customer Reviews</h1>
          <p className="text-text-secondary text-sm mt-1">
            Pull live reviews from Google Maps, upload exports from Zomato/Swiggy, or paste raw text. AI extracts sentiment &amp; themes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Stat label="Total Reviews" value={reviews.length} testid="stat-rev-total" />
        <Stat label="Avg Rating" value={`${avg} / 5`} testid="stat-rev-avg" />
        <Stat label="Positive" value={positive} testid="stat-rev-pos" color="text-state-success" />
        <Stat label="Negative" value={negative} testid="stat-rev-neg" color="text-state-error" />
      </div>

      <Tabs defaultValue="google" className="space-y-6">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="google" data-testid="tab-rev-google">
            <Globe className="h-3 w-3 mr-1 text-state-info" /> Import from Google
          </TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-rev-upload">
            <Sparkles className="h-3 w-3 mr-1 text-state-info" /> Upload File
          </TabsTrigger>
          <TabsTrigger value="paste" data-testid="tab-rev-paste">Paste Reviews</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-rev-list">All Reviews ({reviews.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="google" className="space-y-4">
          <div className="bg-white border border-border rounded-md p-5 space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-base font-semibold text-primary flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Pull live Google reviews into {selectedClient.name}
                </h3>
                <p className="text-xs text-text-secondary mt-1">
                  Search Google Maps for this restaurant, pick the right match, and we&apos;ll import the 5 most-recent reviews via Places API (New). Already-imported reviews are skipped (idempotent).
                </p>
              </div>
              {googleStatus.linked && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid="google-linked-pill">
                  <CheckCircle2 className="h-4 w-4 text-state-success" />
                  <div>
                    <div className="font-semibold text-emerald-900">Linked to Google</div>
                    <div className="text-[10px] font-mono text-emerald-800 break-all">{googleStatus.place_id}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={refreshLinked} disabled={importing} data-testid="google-refresh-btn" className="ml-2">
                    {importing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" /> Pull latest</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={unlinkGoogle}
                    disabled={importing}
                    data-testid="google-unlink-btn"
                    className="text-state-error border-red-200 hover:bg-red-50"
                    title="Wrong restaurant linked? Unlink and search again."
                  >
                    Change / Unlink
                  </Button>
                </div>
              )}
            </div>

            {!googleStatus.linked && (
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1 flex items-center gap-1">
                  <Search className="h-3 w-3" /> Search Google Maps for this restaurant
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQ}
                    onChange={e => setSearchQ(e.target.value)}
                    placeholder="Type your restaurant name + area (e.g. Britannia & Co Ballard Estate)"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm pr-9"
                    data-testid="google-reviews-search-input"
                  />
                  {searching && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-2.5 text-text-muted" />}
                </div>
                {searchResults.length > 0 && (
                  <ul className="mt-2 border border-border rounded-md bg-white max-h-72 overflow-y-auto divide-y divide-border" data-testid="google-reviews-results">
                    {searchResults.map(p => (
                      <li key={p.place_id} className="px-3 py-2 hover:bg-primary/5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-primary truncate">{p.name}</div>
                            <div className="text-[11px] text-text-secondary truncate">{p.address}</div>
                            <div className="text-[11px] text-text-muted mt-0.5">
                              {p.rating ? `${p.rating.toFixed(1)} ★ ` : ''}
                              {p.review_count ? `(${p.review_count.toLocaleString()} reviews)` : 'no reviews'}
                            </div>
                          </div>
                          <Button size="sm" onClick={() => importFromPlace(p.place_id)} disabled={importing} className="bg-primary hover:bg-primary-hover flex-shrink-0" data-testid={`import-place-${p.place_id}`}>
                            {importing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Download className="h-3 w-3 mr-1" /> Import</>}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {placesError && searchQ.length >= 2 && !searching && searchResults.length === 0 && (
                  <div className="mt-2 bg-red-50 border border-red-200 text-red-900 rounded-md p-2 text-[11px] flex items-start gap-2" data-testid="google-search-error">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      Google Places API error: {placesError}
                      {placesError && placesError.toLowerCase().includes('disabled') && (
                        <div className="mt-1">Enable Places API (New) at <a className="underline font-semibold" href="https://console.developers.google.com/apis/api/places.googleapis.com/overview" target="_blank" rel="noreferrer">Cloud Console →</a></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {importResult && (
              <div className={`text-sm p-3 rounded ${importResult.error ? 'bg-red-50 text-state-error' : 'bg-green-50 text-state-success'}`} data-testid="google-import-result">
                {importResult.error
                  ? `Error: ${importResult.error}`
                  : `${importResult.place_name || 'Google'} → fetched ${importResult.fetched}, imported ${importResult.imported}, skipped ${importResult.skipped_duplicates} duplicate(s)`}
              </div>
            )}

            <div className="text-[10px] text-text-muted border-t border-border pt-3">
              <strong>Auto-refresh:</strong> Once linked, the nightly scheduler at 03:00 IST pulls fresh reviews from Google automatically (only new reviews are imported). You can also click <em>Pull latest</em> anytime.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <ModuleUploadZone module="reviews" moduleLabel="Customer Reviews Export"
            clientId={selectedClient.id} API={API} onUploadComplete={load} />
          <PendingImports clientId={selectedClient.id} API={API} module="reviews" onChange={load} />
        </TabsContent>

        <TabsContent value="paste">
          <div className="bg-white border border-border rounded-md p-5 space-y-3">
            <p className="text-sm text-text-secondary">
              Copy reviews directly from Google Maps / Zomato / Swiggy page and paste here. AI will identify each review, extract rating, date, sentiment &amp; themes.
            </p>
            <textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              rows={10}
              placeholder='Example:&#10;&#10;★★★★★ Priya Sharma — 2 weeks ago&#10;Amazing food, the butter chicken was the best in town! Service was quick too.&#10;&#10;★★ Rahul K — 3 weeks ago&#10;Food was cold and waited 40 mins. Disappointed.'
              className="w-full border border-border rounded p-3 text-sm font-mono"
              data-testid="paste-textarea"
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-text-muted">{pasteText.length} characters · AI charges apply per request</p>
              <Button onClick={submitPaste} disabled={pasting || !pasteText.trim()} data-testid="paste-submit">
                {pasting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Extracting...</> : <><Sparkles className="h-4 w-4 mr-1" /> Extract Reviews</>}
              </Button>
            </div>
            {pasteResult && (
              <div className={`text-sm p-3 rounded ${pasteResult.error ? 'bg-red-50 text-state-error' : 'bg-green-50 text-state-success'}`}>
                {pasteResult.error || `Extracted ${pasteResult.rows_extracted} reviews · ${pasteResult.auto_imported} auto-imported, ${pasteResult.pending_review} need review`}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="list">
          <div className="space-y-3">
            {loading ? <div className="p-8 text-center text-text-muted">Loading...</div> :
              reviews.length === 0 ?
                <div className="p-12 text-center text-text-muted bg-white border border-border rounded-md">
                  <MessageSquare className="h-12 w-12 mx-auto opacity-30 mb-3" />
                  No reviews yet. Upload a file or paste reviews above.
                </div>
              :
              reviews.map(r => (
                <div key={r.id} className="bg-white border border-border rounded-md p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-[10px] uppercase tracking-wider bg-background-secondary px-1.5 py-0.5 rounded font-semibold">{r.source}</span>
                        <span className="font-semibold">{r.reviewer}</span>
                        <span className="text-xs text-text-muted">{r.date}</span>
                        <span className="text-state-warning font-semibold">{'★'.repeat(Math.round(r.rating))}{'☆'.repeat(Math.max(0, Math.round(r.rating_max - r.rating)))}</span>
                        <SentimentBadge sentiment={r.sentiment} />
                      </div>
                      <p className="text-sm text-text-primary">{r.text}</p>
                      {r.themes && r.themes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {r.themes.map((t, i) => (
                            <span key={i} className="text-[10px] bg-background-secondary px-1.5 py-0.5 rounded text-text-secondary">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button onClick={() => removeRow(r.id)} variant="ghost" size="sm" className="text-state-error" data-testid={`delete-rev-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            }
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Stat = ({ label, value, color = 'text-primary', testid }) => (
  <div className="bg-white border border-border rounded-md p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</p>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

const SentimentBadge = ({ sentiment }) => {
  const styles = {
    positive: 'bg-green-100 text-state-success',
    negative: 'bg-red-100 text-state-error',
    neutral: 'bg-gray-100 text-text-secondary',
  };
  return (
    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${styles[sentiment] || styles.neutral}`}>
      {sentiment || 'neutral'}
    </span>
  );
};

export default Reviews;
